/* eslint-disable no-void */

import * as vp from 'vopay';
import { useEffect, useState } from 'react';
import axios, { CancelTokenSource } from 'axios';

import vopayApi from '../../services/api.config';
import { useDataFetchIndicator } from '..';
import { usePermissions } from '@/providers/Permissions.provider';
import { useClientData } from '@/providers/ClientData.provider';

type HookProps = {
  api: vp.Api;
};

type ReturnTuple = [vp.WorkFlowsResponse | undefined, (requestParams: vp.PortalUser) => void, () => void];

function useFetchWorkflows({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<vp.PortalUser>();
  const [response, setResponse] = useState<vp.WorkFlowsResponse>();
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const { permissions } = usePermissions();
  const {
    response: { TransactionApprovalWorkflowEnabled },
  } = useClientData();

  useEffect(() => {
    if (!permissions.ViewTransactionApprovalWorkflow || !vp.toBool(TransactionApprovalWorkflowEnabled)) {
      shouldSendRequest && setSendRequest(false);
      return;
    }
    const sendRequest = async (requestParams: vp.PortalUser, cancelTokenSource: CancelTokenSource) => {
      setDataFetchingInfo(true, 'loading some data', 'fullScreen');

      const res = await api.fetchWorkflows(requestParams, cancelTokenSource);

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params, source);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams: vp.PortalUser) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setResponse(undefined);
      setSendRequest(false);
    },
  ];
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(useFetchWorkflows);
